<template>
  <standard-dialog
    v-model="model"
    :title="$t('create_new_limit')"
    max-width="520"
    @save="handleSave"
    :disabled="!valid"
    :loading="saving"
  >
    <v-form ref="form" v-model="valid">
      <div class="text-caption font-weight-medium mb-2 text--secondary">
        {{ $t(scopeChoiceLabelKey) }}
        <span class="error--text">*</span>
      </div>

      <v-row dense class="mb-3">
        <v-col
          v-for="option in choiceOptions"
          :key="option.value"
          :cols="choiceColumnSpan"
        >
          <v-card
            outlined
            :color="currentChoiceValue === option.value ? 'primary' : ''"
            :dark="currentChoiceValue === option.value"
            class="pa-3 d-flex flex-column align-center text-center cursor-pointer"
            style="cursor: pointer; height: 100%"
            @click="currentChoiceValue = option.value"
          >
            <v-icon
              class="mb-1"
              :color="currentChoiceValue === option.value ? 'white' : 'primary'"
              >{{ option.icon }}</v-icon
            >
            <div class="text-body-2 font-weight-medium">
              {{ $t(option.label) }}
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-input
        v-model="currentChoiceValue"
        :rules="[rules.required]"
        hide-details="auto"
        class="mb-3"
        style="display: none"
      />

      <template v-if="hasSelectedChoice">
        <autocomplete
          v-for="field in selectionFields"
          :key="field.key"
          :value="getFieldValue(field.model)"
          :items="field.items"
          :hide-details="false"
          item-text="name"
          item-value="id"
          :label="$t(field.label)"
          :rules="field.rules"
          :loading="loadingOptions"
          :multiple="field.multiple"
          :chips="field.multiple"
          :deletable-chips="field.multiple"
          :required="field.required"
          :class="field.className"
          @input="setFieldValue(field.model, $event)"
        />

        <text-field
          v-model="limitName"
          :label="$t('limit_name')"
          :rules="[rules.required]"
          outlined
          required
          class="mb-1"
        />
      </template>
    </v-form>
  </standard-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasValidation from "@/mixins/hasValidation";
import limitService from "@/services/limit";
import companyService from "@/services/company";
import restaurantService from "@/services/restaurant";
import StandardDialog from "@/components/dialogs/StandardDialog.vue";

export default {
  name: "CreateLimitDialog",
  components: {
    StandardDialog,
  },
  mixins: [hasModel, hasSnackbar, hasValidation],
  props: {
    companyId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      limitName: "",
      saving: false,
      loadingOptions: false,
      adminTargetType: null,
      scopeChoice: null,
      allCompanies: [],
      selectedCompanyId: null,
      selectedRestaurantId: null,
      selectedRestaurantIds: [],
      companies: [],
      restaurants: [],
      allRestaurants: [],
    };
  },
  computed: {
    isAdminMode() {
      return this.$store.state.user.user?.type === "admin" && !this.companyId;
    },
    isRestaurantMode() {
      return !this.companyId && !this.isAdminMode;
    },
    scopeChoiceLabelKey() {
      if (this.isAdminMode) {
        return "limit_type";
      }

      return this.isRestaurantMode
        ? "limit_scope_choice_label"
        : "limit_scope_choice_label_company";
    },
    restaurantId() {
      return this.$store.state.restaurant?.id;
    },
    currentChoiceValue: {
      get() {
        return this.isAdminMode ? this.adminTargetType : this.scopeChoice;
      },
      set(value) {
        if (this.isAdminMode) {
          this.adminTargetType = value;
          return;
        }

        this.scopeChoice = value;
      },
    },
    hasSelectedChoice() {
      return !!this.currentChoiceValue;
    },
    choiceColumnSpan() {
      return this.isAdminMode ? "4" : "6";
    },
    choiceOptions() {
      if (this.isAdminMode) {
        return [
          { value: "company", label: "companies", icon: "mdi-domain" },
          {
            value: "restaurant",
            label: "restaurants",
            icon: "mdi-silverware-fork-knife",
          },
          { value: "combined", label: "combined", icon: "mdi-link-variant" },
        ];
      }

      if (this.isRestaurantMode) {
        return [
          {
            value: "all_companies",
            label: "limit_scope_all_companies",
            icon: "mdi-store",
          },
          {
            value: "specific_company",
            label: "limit_scope_specific_company",
            icon: "mdi-domain",
          },
        ];
      }

      return [
        {
          value: "all_restaurants",
          label: "limit_scope_all_restaurants",
          icon: "mdi-silverware-fork-knife",
        },
        {
          value: "specific_restaurants",
          label: "limit_scope_specific_restaurants",
          icon: "mdi-map-marker-multiple",
        },
      ];
    },
    selectionFields() {
      if (!this.hasSelectedChoice) {
        return [];
      }

      if (this.isAdminMode) {
        if (this.adminTargetType === "company") {
          return [this.buildCompanyField("admin-company", "mb-1")];
        }

        if (this.adminTargetType === "restaurant") {
          return [
            this.buildRestaurantField(
              "admin-restaurant",
              this.allRestaurants,
              false,
              "mb-1"
            ),
          ];
        }

        return [
          this.buildRestaurantField(
            "admin-combined-restaurant",
            this.allRestaurants,
            false,
            "mb-1"
          ),
          ...(this.selectedRestaurantId
            ? [this.buildCompanyField("admin-combined-company", "mb-1")]
            : []),
        ];
      }

      if (this.isRestaurantMode && this.scopeChoice === "specific_company") {
        return [this.buildCompanyField("restaurant-company", "mt-1")];
      }

      if (!this.isRestaurantMode && this.scopeChoice === "specific_restaurants") {
        return [
          this.buildRestaurantField(
            "company-restaurants",
            this.restaurants,
            true,
            "mt-1",
            "select_restaurants",
            "selectedRestaurantIds"
          ),
        ];
      }

      return [];
    },
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.reset();
        this.loadOptions();
      }
    },
    adminTargetType() {
      this.companies = this.allCompanies;
      this.selectedCompanyId = null;
      this.selectedRestaurantId = null;
      this.selectedRestaurantIds = [];
      this.restaurants = [];
    },
    scopeChoice() {
      if (this.isAdminMode) return;
      if (this.isRestaurantMode) {
        this.selectedCompanyId = null;
      } else {
        this.selectedRestaurantIds = [];
      }
    },
    async selectedRestaurantId(newValue) {
      if (!this.isAdminMode || this.adminTargetType !== "combined") return;

      this.selectedCompanyId = null;

      if (!newValue) {
        this.companies = this.allCompanies;
        return;
      }

      await this.loadRestaurantCompanies(newValue);
    },
  },
  methods: {
    normalizeCompaniesResponse(data) {
      return data?.companies || data?.data || data || [];
    },
    buildCompanyField(key, className = "mb-1") {
      return {
        key,
        model: "selectedCompanyId",
        items: this.companies,
        label: "select_company",
        rules: [this.rules.required],
        multiple: false,
        required: true,
        className,
      };
    },
    buildRestaurantField(
      key,
      items,
      multiple = false,
      className = "mb-1",
      label = "select_restaurant",
      model = "selectedRestaurantId"
    ) {
      return {
        key,
        model,
        items,
        label,
        rules: [multiple ? this.rules.requiredArray : this.rules.required],
        multiple,
        required: true,
        className,
      };
    },
    getFieldValue(model) {
      return this[model];
    },
    setFieldValue(model, value) {
      this[model] = value;
    },
    reset() {
      this.valid = false;
      this.limitName = "";
      this.adminTargetType = null;
      this.scopeChoice = null;
      this.companies = this.allCompanies;
      this.selectedCompanyId = null;
      this.selectedRestaurantId = null;
      this.selectedRestaurantIds = [];
      this.restaurants = [];
      this.$nextTick(() => this.$refs.form?.resetValidation());
    },
    async loadOptions() {
      this.loadingOptions = true;
      try {
        if (this.isAdminMode) {
          const [{ data: companies }, { data: restaurants }] = await Promise.all([
            companyService.getCompanies(),
            restaurantService.getAllRestaurants(),
          ]);
          this.allCompanies = this.normalizeCompaniesResponse(companies);
          this.companies = this.normalizeCompaniesResponse(companies);
          this.allRestaurants = restaurants || [];
        } else if (this.isRestaurantMode) {
          const { data } = await companyService.getCompanies();
          this.companies = this.normalizeCompaniesResponse(data);
        } else {
          const { data } = await companyService.getCompanyRestaurants(this.companyId);
          this.restaurants = data?.data || data || [];
        }
      } catch (_) {
        // non-critical
      } finally {
        this.loadingOptions = false;
      }
    },
    async loadRestaurantCompanies(restaurantId) {
      this.loadingOptions = true;
      try {
        const { data } = await restaurantService.getRestaurantCompanies(restaurantId);
        this.companies = this.normalizeCompaniesResponse(data);
      } catch (_) {
        this.companies = [];
      } finally {
        this.loadingOptions = false;
      }
    },
    async handleSave() {
      if (!this.$refs.form.validate()) return;

      this.saving = true;
      try {
        let payload;

        if (this.isAdminMode) {
          if (this.adminTargetType === "restaurant") {
            payload = {
              name: this.limitName,
              scope: "restaurant",
              restaurant_id: this.selectedRestaurantId,
            };
          } else if (this.adminTargetType === "company") {
            payload = {
              name: this.limitName,
              scope: "company",
              company_id: parseInt(this.selectedCompanyId),
            };
          } else {
            payload = {
              name: this.limitName,
              scope: "combined",
              company_id: parseInt(this.selectedCompanyId),
              allowed_restaurants: [this.selectedRestaurantId],
            };
          }
        } else if (this.isRestaurantMode) {
          if (this.scopeChoice === "all_companies") {
            payload = {
              name: this.limitName,
              scope: "restaurant",
              restaurant_id: this.restaurantId,
            };
          } else {
            payload = {
              name: this.limitName,
              scope: "combined",
              company_id: this.selectedCompanyId,
              allowed_restaurants: [this.restaurantId],
            };
          }
        } else if (this.scopeChoice === "all_restaurants") {
          payload = {
            name: this.limitName,
            scope: "company",
            company_id: parseInt(this.companyId),
          };
        } else {
          payload = {
            name: this.limitName,
            scope: "combined",
            company_id: parseInt(this.companyId),
            allowed_restaurants: this.selectedRestaurantIds,
          };
        }

        const { data } = await limitService.createLimit(payload);
        this.showSuccessSnackbar(this.$t("limit_created_successfully"));
        this.$emit("created", data.data);
        this.model = false;
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_creating_limit"));
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
